/* eslint-disable @lwc/lwc/no-document-query */
import { createStore, selectParams } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { FORCE_COPILOT_LISTING_TYPE } from '~/constants';
import { IS_DEVELOPMENT, SALESFORCE_ORG_62_ACCOUNT_ID } from '~/modules/config';
import { generateLegacyListingUrl } from '~/modules/helpers/routingHelper';
import { ReviewSummary } from '~/modules/services/reviewService';
import { recentItemStore } from '~/modules/stores/recentItemStore';
import { routerStore } from '~/modules/stores/routerStore';
import { getCookie } from '~/modules/utilities/cookies';
import { logger, throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
import { notFoundError } from '~/modules/utilities/errorUtilities';
import { isSupportedTechnologyType, isValidListingId } from '~/modules/utilities/listing_utils';
import { ListingResponse, listingService } from '../services/listingService';

const APP_LISTINGS_TECHNOLOGIES = [
    'force/package',
    'force/bolt',
    'force/flow',
    'force/lightning',
    'quip/package',
    FORCE_COPILOT_LISTING_TYPE,
];

export interface ListingState extends State {
    // TODO: Enforce a maximum cache size for the map, 128 seems like a reasonable number of listings to cache
    listings: Map<string, ListingResponse>;
    isLoading: boolean;
}

const initialState: ListingState = {
    listings: new Map<string, ListingResponse>(),
    isLoading: false,
};

export const selectListing = (state: ListingState, listingId: string): ListingResponse | null => {
    return state.listings.get(listingId) ?? null;
};

export const selectListingFromUrl = (state: ListingState): ListingResponse | null => {
    const listingId = selectParams(routerStore.getState())?.listingId;
    if (isValidListingId(listingId)) {
        return selectListing(state, listingId);
    }
    return null;
};

export const selectListings = (state: ListingState, listingIds: string[]): ListingResponse[] => {
    if (listingIds) {
        return listingIds.map((listingId) => selectListing(state, listingId)).filter(Boolean) as ListingResponse[];
    }
    return [];
};

export function selectIsLinkListing(listing: ListingResponse): boolean {
    return listing.technology === 'b2c-commerce/package' || listing.technology === 'b2c-commerce/api';
}

export function selectIsSalesforceProduct(listing: ListingResponse): boolean {
    return listing.partnerLookup === SALESFORCE_ORG_62_ACCOUNT_ID;
}

export function selectIsAppListing(listing: ListingResponse): boolean {
    return APP_LISTINGS_TECHNOLOGIES.includes(listing.technology);
}

export const listingStore = createStore(
    // State
    initialState,
    // Action functions
    (set: SetState<ListingState>, get: GetState<ListingState>) => ({
        async fetchListing(id: string) {
            if (!id) {
                throwDevelopmentOrLog(new Error(`Missing required id parameter: ${id}`));
                return;
            }

            const existingListing = selectListing(get(), id);
            if (existingListing) {
                return;
            }

            try {
                set(function setIsLoading(draft) {
                    draft.isLoading = true;
                });

                const { listing } = await listingService.getListing(id, getCookie('oaf_canary'));
                set(function setListing(draft: ListingState) {
                    draft.isLoading = false;
                    draft.listings.set(id, listing);
                });
            } catch (error) {
                console.warn(`Failed to find listing with ID '${id}' in OAF`);
                set(function updateListings(draft: ListingState) {
                    draft.isLoading = false;
                });
                throw error;
            }
        },
        upsertListing(listingId: string, listing: ListingResponse) {
            set(function upsertListing(draft: ListingState) {
                draft.listings.set(listingId, listing);
            });
        },
        updateReviewSummary(id: string, summary: ReviewSummary) {
            set(function updateReviewSummary(draft) {
                const listing = draft.listings.get(id);
                // @ts-expect-error * Automated Type Error Fix
                listing.reviewsSummary.reviewCount = summary.reviewCount;
                // @ts-expect-error * Automated Type Error Fix
                listing.reviewsSummary.averageRating = summary.averageRating;
            });
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    // Metadata
    'listingStore',
    IS_DEVELOPMENT,
);

export async function setOrFetchListing(listingId = '', context: string): Promise<any> {
    try {
        if (!isValidListingId(listingId)) {
            notFoundError(context);
            return false;
        }

        let listing = selectListing(listingStore.getState(), listingId);

        if (!listing) {
            await listingStore.fetchListing(listingId);
            listing = selectListing(listingStore.getState(), listingId);
            if (!listing) {
                throw new Error();
            }
        }

        // If the new listing detail doesn't support this listing type, redirect to legacy site
        if (!isSupportedTechnologyType(listing)) {
            const url = generateLegacyListingUrl(listing);
            logger.warn(
                `Attempt to access unsupported listing with ID '${listingId}' and Technology '${listing.technology}', redirecting to legacy page: ${url}`,
            );
            window.location.assign(url);
            return false;
        }

        if (listing.public) {
            recentItemStore.updateRecentItems('test-user-id', {
                listingId: listing.oafId ?? listing.external_id ?? '',
                timestamp: Date.now(),
            });
        }

        return listing;
    } catch (error) {
        const url = generateLegacyListingUrl();
        logger.error(`Failed to find listing with ID '${listingId}' in OAF, redirecting to legacy page: ${url}`);
        window.location.assign(url);
    }
    return false;
}
